import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";

export default function TutorialDetail() {
  const { id } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const category = searchParams.get('cat') || 'ai';
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();

  const allTutorials = {
    ai: [
      {
        title: "Getting Started with Claude for Academic Research",
        level: "Beginner",
        time: "15 min",
        description: "Learn how to prompt Claude effectively for literature reviews, summaries, and structured outlines.",
        sections: [
          { title: "What is Claude?", content: "Claude is an advanced AI assistant created by Anthropic. It excels at understanding context, providing nuanced explanations, and maintaining academic rigor in responses." },
          { title: "Setting Up Your Account", content: "Visit claude.ai and create an account. No coding knowledge required. Claude is free for basic use with optional premium features." },
          { title: "Best Practices for Research", content: "Always provide context about your academic level. Ask Claude to cite sources when applicable. Use follow-up questions to refine responses." },
          { title: "Example Prompts", content: "1) 'Summarize the key arguments of [paper] in 200 words'\n2) 'Create an outline for a paper on [topic]'\n3) 'Explain [complex concept] in simple terms'" },
        ],
      },
    ],
    cite: [
      {
        title: "Setting Up Zotero with APA 7th Edition",
        level: "Beginner",
        time: "20 min",
        description: "Install, configure, and link Zotero to Word or Google Docs — with automatic APA citation generation.",
        sections: [
          { title: "Installation", content: "Download Zotero from zotero.org. Install the desktop app and browser connector. Create a free account to sync across devices." },
          { title: "Adding Sources", content: "Use the browser connector to save articles, web pages, and books. Manually add sources by entering ISBN or DOI. Import from other reference managers." },
          { title: "Creating a Collection", content: "Organize sources by project. Create folders and subfolders. Use tags for additional organization and quick searching." },
          { title: "Citation Styles", content: "Set your preferred citation style to APA 7th. Generate bibliographies automatically. Export citations in multiple formats." },
        ],
      },
    ],
    data: [
      {
        title: "Running Descriptive Statistics in SPSS",
        level: "Intermediate",
        time: "30 min",
        description: "Import data, clean variables, and produce frequency tables, means, and standard deviations.",
        sections: [
          { title: "Importing Data", content: "Open SPSS and import data from Excel or CSV. Map variable names and types. Check for missing values and outliers." },
          { title: "Data Cleaning", content: "Define variable properties (scale, ordinal, nominal). Create value labels for categorical data. Identify and handle missing values appropriately." },
          { title: "Running Descriptive Stats", content: "Navigate to Analyze > Descriptive Statistics > Frequencies. Select variables and choose statistics options. Request charts if needed." },
          { title: "Interpreting Results", content: "Understand frequency distributions, mean, median, mode, standard deviation, and variance. Create tables for reporting." },
        ],
      },
    ],
    pm: [
      {
        title: "Planning Your Dissertation with Notion",
        level: "All Levels",
        time: "25 min",
        description: "Build a Gantt-style timeline, chapter tracker, and reading log inside Notion.",
        sections: [
          { title: "Setting Up Notion", content: "Create a workspace for your dissertation. Use database features for organizing chapters, sources, and tasks." },
          { title: "Creating Databases", content: "Build a chapters database with status, deadlines, and word counts. Set up a reading list with sources and key quotes." },
          { title: "Timeline & Gantt View", content: "Use the timeline view to visualize deadlines. Connect dependent tasks. Set milestones and checkpoints." },
          { title: "Templates & Automation", content: "Use templates for recurring tasks. Create formulas to auto-calculate progress. Set database relations for cross-referencing." },
        ],
      },
    ],
  };

  const tutorials = allTutorials[category] || [];
  const tutorial = tutorials[parseInt(id)] || tutorials[0] || { title: "Tutorial", sections: [] };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    
    :root {
      --ink: #0f1117;
      --ink-soft: #4a4f5e;
      --bg: #f7f8fa;
      --surface: #ffffff;
      --border: #e4e6ed;
      --radius: 14px;
      --grad: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    }
    
    [data-theme="dark"] {
      --ink: #f7f8fa;
      --ink-soft: #cbd5e1;
      --bg: #0f1117;
      --surface: #1a1f3a;
      --border: #2d3748;
    }
    
    body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--ink); line-height: 1.7; }
    
    nav {
      position: sticky; top: 0; z-index: 100;
      background: rgba(247,248,250,.88);
      backdrop-filter: blur(14px);
      border-bottom: 1px solid var(--border);
      padding: 0 2rem;
      display: flex; align-items: center; justify-content: space-between;
      height: 64px;
    }
    [data-theme="dark"] nav { background: rgba(15,17,23,.88); }
    
    .nav-back { cursor: pointer; font-size: 1.2rem; color: var(--ink); transition: opacity .2s; }
    .nav-back:hover { opacity: .6; }
    
    .nav-theme-toggle {
      background: transparent; border: none; cursor: pointer;
      font-size: 1.2rem; color: var(--ink);
      padding: .4rem; border-radius: 6px;
      transition: background .2s;
    }
    .nav-theme-toggle:hover { background: rgba(102,126,234,.1); }
    
    .header {
      padding: 3rem 2rem;
      background: linear-gradient(180deg, rgba(102,126,234,.08) 0%, rgba(118,75,162,.04) 100%);
    }
    
    h1 {
      font-family: 'DM Serif Display', serif;
      font-size: 2rem;
      margin-bottom: 1rem;
      background: var(--grad);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .meta {
      display: flex;
      gap: 1.5rem;
      color: var(--ink-soft);
      margin-top: 1rem;
    }
    
    .meta-item {
      display: flex;
      align-items: center;
      gap: .3rem;
    }
    
    .content {
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
    }
    
    .description {
      background: var(--surface);
      border-left: 4px solid;
      border-image: var(--grad) 1;
      padding: 1.5rem;
      border-radius: var(--radius);
      margin-bottom: 2rem;
      color: var(--ink-soft);
      font-size: 1.05rem;
    }
    
    h2 {
      font-family: 'DM Serif Display', serif;
      font-size: 1.6rem;
      margin: 2.5rem 0 1rem;
      color: var(--ink);
    }
    
    h3 {
      font-size: 1.2rem;
      margin: 1.5rem 0 .8rem;
      color: var(--ink);
    }
    
    p {
      color: var(--ink-soft);
      margin-bottom: 1rem;
    }
    
    .section {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }
    
    .section h3 {
      margin-top: 0;
      border-bottom: 2px solid var(--border);
      padding-bottom: .8rem;
    }
    
    .cta-button {
      background: var(--grad);
      color: #fff;
      padding: 1rem 2rem;
      border: none;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all .2s;
      display: inline-block;
      text-decoration: none;
      margin-top: 2rem;
    }
    
    .cta-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102,126,234,.4);
    }
    
    footer {
      border-top: 1px solid var(--border);
      padding: 2rem;
      text-align: center;
      color: var(--ink-soft);
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <nav>
        <div className="nav-back" onClick={() => navigate(`/category/${category}`)}>← Back</div>
        <button className="nav-theme-toggle" onClick={toggleTheme}>
          {isDarkMode ? '☀️' : '🌙'}
        </button>
      </nav>

      <div className="header">
        <h1>{tutorial.title}</h1>
        <div className="meta">
          <div className="meta-item">📚 {tutorial.level}</div>
          <div className="meta-item">⏱ {tutorial.time}</div>
          <div className="meta-item">🏷️ {category.toUpperCase()}</div>
        </div>
      </div>

      <div className="content">
        <div className="description">{tutorial.description}</div>

        {tutorial.sections.map((section, idx) => (
          <div key={idx} className="section">
            <h3>{section.title}</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{section.content}</p>
          </div>
        ))}

        <button className="cta-button" onClick={() => navigate('/tools/assignment-evaluator')}>
          Get Feedback with Assignment Evaluator ✨
        </button>
      </div>

      <footer>
        <p>© 2026 flycodeinv.com · All Rights Reserved</p>
      </footer>
    </>
  );
}
